/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { Fragment } from 'react'
import { sortBy } from 'lodash'

import { EuiBadge, EuiIcon, useEuiTheme } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { DeploymentTemplateInfoV2 } from '@modules/cloud-api/v1/types'
import type { RegionId } from '@modules/ui-types'
import { CuiTable } from '@modules/cui/Table'
import { CuiTimeAgo } from '@modules/cui/TimeAgo'
import type { CuiTableColumn } from '@modules/cui/Table'
import { CuiRouterLink } from '@modules/cui/RouterButton'

import { isPureIndexCurationTemplate } from '@/lib/stackDeployments/indexManagement'

import ProductIcons from '../components/ProductIcons'
import {
  topologyEditDeploymentTemplateUrl,
  topologyViewDeploymentTemplateUrl,
} from '../../../../lib/urlBuilder'
import { isDeprecatedConfig } from '../../../../lib/instanceConfigurations/instanceConfiguration'

import type { FunctionComponent } from 'react'

type Props = {
  deploymentTemplates?: DeploymentTemplateInfoV2[]
  regionId: RegionId
}

const DeploymentTemplatesTable: FunctionComponent<Props> = ({ deploymentTemplates, regionId }) => {
  const theme = useEuiTheme()

  const getDeploymentTemplateTableRowStyles = (deploymentTemplate: DeploymentTemplateInfoV2) => {
    const hasDeprecatedConfigs = deploymentTemplate.instance_configurations.some(isDeprecatedConfig)

    const warningBackground =
      isPureIndexCurationTemplate({ deploymentTemplate }) || hasDeprecatedConfigs
    return css(
      warningBackground && {
        '&, &:hover': {
          backgroundColor: theme.euiTheme.colors.backgroundLightWarning,
        },
      },
    )
  }

  const anyDeprecated =
    deploymentTemplates &&
    deploymentTemplates.some(({ instance_configurations = [] }) =>
      instance_configurations.some(isDeprecatedConfig),
    )

  const anyPureCurationTemplates =
    deploymentTemplates &&
    deploymentTemplates.some((deploymentTemplate) =>
      isPureIndexCurationTemplate({ deploymentTemplate }),
    )

  const columns: Array<CuiTableColumn<DeploymentTemplateInfoV2>> = [
    {
      verticalAlign: `top`,
      label: <FormattedMessage id='deployment-template-table.header-name' defaultMessage='Name' />,
      render: (template) => (
        <Fragment>
          <CuiRouterLink to={topologyViewDeploymentTemplateUrl(regionId, template.id!)}>
            {template.name}
          </CuiRouterLink>
          <div>{template.description}</div>
        </Fragment>
      ),
      sortKey: `name`,
    },

    {
      width: `12rem`,
      verticalAlign: `top`,
      label: (
        <FormattedMessage id='deployment-template-table.header-types' defaultMessage='Products' />
      ),
      render: (template) => <ProductIcons deploymentTemplateInfo={template} />,
    },

    {
      width: `9rem`,
      verticalAlign: `top`,
      label: (
        <FormattedMessage
          id='deployment-template-table.header-min-version'
          defaultMessage='Min. version'
        />
      ),
      render: (template) => <div>{template.min_version}</div>,
    },

    {
      width: `8rem`,
      verticalAlign: `top`,
      label: (
        <FormattedMessage
          id='deployment-template-table.header-createdBy'
          defaultMessage='Created by'
        />
      ),
      render: (template) => (
        <EuiBadge color='hollow'>
          {template.system_owned ? (
            <FormattedMessage id='deployment-template-table.table.system' defaultMessage='System' />
          ) : (
            <FormattedMessage id='deployment-template-table.table.user' defaultMessage='User' />
          )}
        </EuiBadge>
      ),
      sortKey: `system_owned`,
    },

    {
      width: `12rem`,
      verticalAlign: `top`,
      label: (
        <FormattedMessage
          id='deployment-template-table.header-lastUpdated'
          defaultMessage='Last updated'
        />
      ),
      render: (template) => template.source && <CuiTimeAgo date={template.source.date} />,
      sortKey: `source.date`,
    },
  ]

  if (anyDeprecated || anyPureCurationTemplates) {
    columns.push({
      width: `10rem`,
      verticalAlign: `top`,
      label: (
        <FormattedMessage
          id='topologyinstanceConfigurations.table.additionalInformationHeader'
          defaultMessage='Additional information'
        />
      ),
      render: (deploymentTemplate) => {
        const hasILM = isPureIndexCurationTemplate({ deploymentTemplate })
        const hasDeprecatedConfigs =
          deploymentTemplate.instance_configurations.some(isDeprecatedConfig)

        return (
          <div style={{ maxWidth: `8rem` }}>
            {hasILM && (
              <CuiRouterLink
                to={{
                  pathname: topologyEditDeploymentTemplateUrl(regionId, deploymentTemplate.id!),
                  state: { showStep: `indexManagement` },
                }}
              >
                <FormattedMessage
                  id='deployment-template-table.configure-ilm'
                  defaultMessage='Configure ILM'
                />
                <EuiIcon type='arrowRight' />
              </CuiRouterLink>
            )}
            {hasDeprecatedConfigs && (
              <CuiRouterLink
                to={{
                  pathname: topologyEditDeploymentTemplateUrl(regionId, deploymentTemplate.id!),
                  state: { showStep: `instances` },
                }}
              >
                <FormattedMessage
                  id='deployment-template-table.update-configuration'
                  defaultMessage='Update deprecated configuration'
                />
                <EuiIcon type='arrowRight' />
              </CuiRouterLink>
            )}
          </div>
        )
      },
    })
  }

  return (
    <CuiTable<DeploymentTemplateInfoV2>
      rows={sortBy(deploymentTemplates, ['system_owned', 'name'])}
      columns={columns}
      getRowId={(deploymentTemplate) => deploymentTemplate.id!}
      rowStyle={getDeploymentTemplateTableRowStyles}
      initialLoading={!deploymentTemplates}
    />
  )
}

export default DeploymentTemplatesTable
